import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MessageSquare, Send } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';

import { GET_LABORATORY_BY_USER } from '../../api/laboratory-api';
import { GET_TICKETS_BY_USER, CREATE_TICKET, ADD_MESSAGE_TO_TICKET } from '../../api/messages-api';
import { authApi } from '../../api/auth-api';

const getStatusDisplay = (status) => {
  switch (status) {
    case 'OPEN': return { text: 'Ouvert', className: 'bg-blue-100 text-blue-700' };
    case 'IN_PROGRESS': return { text: 'En cours', className: 'bg-yellow-100 text-yellow-700' };
    case 'RESOLVED': return { text: 'Résolu', className: 'bg-green-100 text-green-700' };
    case 'CLOSED': return { text: 'Archivé', className: 'bg-gray-100 text-gray-600' };
    default: return { text: status, className: 'bg-gray-100 text-gray-600' };
  }
};

const MessagesPage = () => {
  const [messageInput, setMessageInput] = useState('');
  const [newMessageInput, setNewMessageInput] = useState('');
  const [pendingMessages, setPendingMessages] = useState([]);

  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  const userData = authApi.getUserData();
  const userId = userData?.id;

  const { data: laboratoryData } = useQuery(GET_LABORATORY_BY_USER, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network'
  });
  const laboratory = laboratoryData?.laboratoryByUserId;

  const { data: ticketsData, loading: ticketsLoading, refetch: refetchTickets } = useQuery(GET_TICKETS_BY_USER, {
    variables: { userId: userId || '' },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000
  });

  const [createTicket, { loading: creatingTicket }] = useMutation(CREATE_TICKET, {
    onCompleted: () => {
      setNewMessageInput('');
      refetchTickets();
    },
    onError: (error) => {
      toast.error(`Erreur: ${error.message}`);
    }
  });

  const [addMessageToTicket, { loading: sendingMessage }] = useMutation(ADD_MESSAGE_TO_TICKET, {
    onCompleted: () => {
      setPendingMessages([]);
      setMessageInput('');
      refetchTickets();
    },
    onError: (error) => {
      setPendingMessages([]);
      toast.error(`Erreur lors de l'envoi: ${error.message}`);
    }
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [pendingMessages, ticketsData]);

  const tickets = ticketsData?.ticketsByUser || [];

  // Pick the most recent ticket (prefer active, then any)
  const sortedTickets = [...tickets].sort(
    (a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
  );
  const activeTicket = sortedTickets.find(t => t.status !== 'CLOSED') || sortedTickets[0] || null;

  const allMessages = [
    ...(activeTicket?.messages || []),
    ...pendingMessages
  ];

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !activeTicket || !userId) return;

    const optimistic = {
      id: `pending-${Date.now()}`,
      content: messageInput.trim(),
      adminReply: null,
      date: new Date().toISOString(),
      userId,
      user: { firstName: userData?.firstName || '', lastName: userData?.lastName || '' }
    };
    const textToSend = messageInput.trim();
    setPendingMessages(prev => [...prev, optimistic]);
    setMessageInput('');

    await addMessageToTicket({
      variables: {
        addMessageInput: {
          ticketId: activeTicket.id,
          content: textToSend,
          userId
        }
      }
    });
  };

  const handleStartConversation = async (e) => {
    e.preventDefault();
    if (!newMessageInput.trim() || !userId) return;
    await createTicket({
      variables: {
        createTicketInput: {
          subject: 'Support Mazid',
          content: newMessageInput.trim(),
          category: 'GENERAL',
          priority: 'NORMAL',
          userId
        }
      }
    });
  };

  return (
    <div className="flex flex-col" style={{ margin: '-1.5rem', width: 'calc(100% + 3rem)', height: '100vh', overflow: 'hidden' }}>
      {/* Page Header */}
      <div className="flex-shrink-0 px-6 py-3 flex items-center justify-between border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
            <MessageSquare size={16} className="text-green-700" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-gray-900">Support Mazid</h1>
            {laboratory && <p className="text-xs text-gray-400">{laboratory.nom}</p>}
          </div>
        </div>
        {activeTicket && (() => {
          const s = getStatusDisplay(activeTicket.status);
          return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.className}`}>{s.text}</span>;
        })()}
      </div>

      {/* Conversation area */}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 flex flex-col bg-gray-50 min-h-0">
        {ticketsLoading && tickets.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-green-700 border-t-transparent" />
          </div>
        ) : activeTicket ? (
          <>
            {/* Messages — only this scrolls */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {allMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <MessageSquare size={32} className="mb-2 opacity-40" />
                  <p className="text-sm">Aucun message</p>
                </div>
              ) : (
                allMessages.map((message) => {
                  const isAdminReply = !!message.adminReply;
                  return (
                    <div key={message.id} className={`flex ${isAdminReply ? 'justify-start' : 'justify-end'}`}>
                      {isAdminReply ? (
                        <div className="max-w-sm lg:max-w-md">
                          <p className="text-xs font-medium text-green-700 mb-1 ml-1">Admin Mazid</p>
                          <div className="px-4 py-2.5 rounded-2xl rounded-tl-sm bg-white border border-gray-200 shadow-sm">
                            <p className="text-sm text-gray-800">{message.adminReply}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {format(new Date(message.date), 'dd MMM yyyy, HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="max-w-sm lg:max-w-md">
                          <div className={`px-4 py-2.5 rounded-2xl rounded-tr-sm text-white ${
                            message.id.startsWith('pending-') ? 'bg-green-500' : 'bg-green-700'
                          }`}>
                            <p className="text-sm">{message.content}</p>
                            <p className="text-xs text-green-200 mt-1">
                              {message.id.startsWith('pending-')
                                ? 'envoi...'
                                : format(new Date(message.date), 'dd MMM yyyy, HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-white">
              <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                <input
                  ref={messageInputRef}
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Écrivez votre message..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  disabled={sendingMessage}
                />
                <button
                  type="submit"
                  disabled={!messageInput.trim() || sendingMessage || !userId}
                  className="flex-shrink-0 p-2.5 rounded-xl bg-green-700 text-white hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {sendingMessage
                    ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    : <Send size={16} />}
                </button>
              </form>
            </div>
          </>
        ) : (
          /* No ticket yet — start conversation */
          <>
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mb-4">
                <MessageSquare size={24} className="text-green-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Contacter le support Mazid</h3>
              <p className="text-xs text-gray-400 max-w-xs">
                Écrivez votre message ci-dessous. Notre équipe vous répondra dans les meilleurs délais.
              </p>
            </div>
            <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-white">
              <form onSubmit={handleStartConversation} className="flex items-center space-x-2">
                <input
                  autoFocus
                  type="text"
                  value={newMessageInput}
                  onChange={(e) => setNewMessageInput(e.target.value)}
                  placeholder="Décrivez votre demande..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={!newMessageInput.trim() || creatingTicket || !userId}
                  className="flex-shrink-0 p-2.5 rounded-xl bg-green-700 text-white hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {creatingTicket
                    ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    : <Send size={16} />}
                </button>
              </form>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
